
import { computed, defineComponent, onUnmounted, reactive, ref, watch } from "vue"
import MarketingCampaign, {
    MarketingArticleProposal,
    MarketingArticlePublicationOutcomes,
} from "packs/models/MarketingCampaign"

const articleStatusPollIntervalMs = 20_000

export default defineComponent({
    name: "ArticlePublicationPanel",
    props: {
        campaignId: { type: String, required: true },
        article: { type: Object as () => MarketingArticleProposal, required: true },
    },
    emits: ["update", "new"],
    setup(props, { emit }) {
        const submitting = ref(false)
        const confirmed = ref(false)
        const errorMessage = ref("")
        const migrationConfirmed = ref(false)
        const migrationReason = ref("")
        const outcomes = ref<MarketingArticlePublicationOutcomes | null>(null)
        const outcomesLoading = ref(false)
        const observationSaving = ref(false)
        const now = new Date()
        const observation = reactive({
            query: String(props.article.ai_answer_plan?.primary_question?.observed_query || ""),
            observed_at: new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 16),
            engine: "google" as "google" | "bing" | "chatgpt" | "gemini" | "perplexity" | "copilot" | "other",
            locale: "ja-JP",
            country: "JP",
            language: "ja",
            device: "desktop" as "desktop" | "mobile" | "tablet",
            search_surface: "ai_overview" as "ai_overview" | "ai_mode" | "organic_search",
            citation_status: "unknown" as "cited" | "not_cited" | "unknown",
            citation_url: "",
            observation_conditions: {
                signed_in: false,
                browser_mode: "",
            },
            notes: "",
        })
        let pollTimer: ReturnType<typeof setTimeout> | null = null
        let pollCount = 0

        const checks = computed(() => props.article.publication_preflight_result?.checks || [])
        const conflicts = computed(() => props.article.publication_conflicts || [])
        const hasPreflight = computed(() => !!props.article.publication_preflight_at)
        const automaticItems = computed(() => props.article.publication_preflight_result?.automatic_items || [])
        const manualItems = computed(() => props.article.publication_preflight_result?.manual_items || [])
        const strictQualityGate = computed(() => props.article.editorial_quality_gate?.strict === true)
        const migrationRequired = computed(() =>
            !strictQualityGate.value &&
            !props.article.quality_plan_migration_approved_at &&
            !(props.article.wordpress_post_status == "publish" && !props.article.published_at)
        )
        const reviewDisabled = computed(() =>
            submitting.value ||
            (migrationRequired.value && (!migrationConfirmed.value || !migrationReason.value.trim()))
        )
        const canPublish = computed(() =>
            props.article.publication_status == "draft" &&
            props.article.publication_preflight_result?.ready === true &&
            conflicts.value.length == 0 &&
            !!props.article.publication_preflight_digest
        )
        const auditWarnings = computed(() => props.article.seo_audit_result?.warnings || [])
        const latestAudit = computed(() => outcomes.value?.audits?.[0] || null)
        const auditCategories = computed(() => [
            { key: "technical_eligibility", label: "技術的掲載資格", value: latestAudit.value?.technical_eligibility },
            { key: "answer_content_readiness", label: "回答内容の準備状況", value: latestAudit.value?.answer_content_readiness },
            { key: "trust_signals", label: "信頼情報", value: latestAudit.value?.trust_signals },
            { key: "index_status", label: "インデックス状態", value: latestAudit.value?.index_status },
            { key: "measurement_status", label: "計測状態", value: latestAudit.value?.measurement_status },
        ])
        const auditStatusLabel = computed(() => {
            const primaryCategoryBlocked = [
                latestAudit.value?.technical_eligibility?.status,
                latestAudit.value?.answer_content_readiness?.status,
            ].includes("blocked")
            const status = primaryCategoryBlocked
                ? "blocked"
                : latestAudit.value?.status ||
                  outcomes.value?.article_seo_audit_status ||
                  props.article.seo_audit_status ||
                  "not_started"
            return ({
                not_started: "未開始",
                pending: "反映待ち・確認中",
                verified: "確認済み",
                warning: "警告あり",
                blocked: "ブロック",
                failed: "確認失敗",
                manual_required: "手動確認が必要",
            } as Record<string, string>)[status] || "未確認"
        })

        function applyResult(result: any) {
            if (result?.error) {
                errorMessage.value = result.message || "処理を完了できませんでした。"
                return false
            }
            if (result?.article) {
                errorMessage.value = ""
                confirmed.value = false
                emit("update", result.article)
                return true
            }
            errorMessage.value = "記事の最新状態を取得できませんでした。"
            return false
        }

        async function reviewPublication() {
            if (reviewDisabled.value) return
            submitting.value = true
            const result = migrationRequired.value
                ? await MarketingCampaign.reviewArticlePublication(
                    props.campaignId,
                    props.article.id,
                    { confirmation: migrationConfirmed.value, reason: migrationReason.value }
                )
                : await MarketingCampaign.reviewArticlePublication(props.campaignId, props.article.id)
            applyResult(result)
            submitting.value = false
        }

        async function adoptWordpress() {
            if (submitting.value) return
            submitting.value = true
            applyResult(await MarketingCampaign.adoptWordpressArticle(props.campaignId, props.article.id))
            submitting.value = false
        }

        async function publishArticle() {
            if (!canPublish.value || !confirmed.value || submitting.value) return
            const message = "この記事をWordPress上で公開します。\n公開後は一般のユーザーと検索エンジンから閲覧可能になります。"
            if (!window.confirm(message)) return

            submitting.value = true
            const result = await MarketingCampaign.publishArticle(
                props.campaignId,
                props.article.id,
                props.article.publication_preflight_digest || ""
            )
            const applied = applyResult(result)
            submitting.value = false
            if (applied) schedulePoll()
        }

        async function recheckSeo() {
            if (submitting.value || props.article.seo_audit_status == "pending") return
            submitting.value = true
            const applied = applyResult(await MarketingCampaign.recheckArticleSeo(props.campaignId, props.article.id))
            submitting.value = false
            if (applied) schedulePoll()
        }

        async function loadOutcomes() {
            if (outcomesLoading.value) return
            outcomesLoading.value = true
            const result = await MarketingCampaign.fetchArticlePublicationOutcomes(
                props.campaignId,
                props.article.id
            )
            if (result) outcomes.value = result
            else errorMessage.value = "公開監査と計測結果を取得できませんでした。"
            outcomesLoading.value = false
        }

        async function saveObservation() {
            if (observationSaving.value) return
            observationSaving.value = true
            const result = await MarketingCampaign.createArticleSearchObservation(
                props.campaignId,
                props.article.id,
                {
                    ...observation,
                    observed_at: new Date(observation.observed_at).toISOString(),
                    citation_url: observation.citation_url || undefined,
                    notes: observation.notes || undefined,
                }
            )
            if (result?.error) errorMessage.value = result.message
            else {
                errorMessage.value = ""
                await loadOutcomes()
            }
            observationSaving.value = false
        }

        function categoryMessages(category: any) {
            return (category?.checks || []).filter((check: any) => check.status !== "passed" && check.message).map((check: any) => check.message)
        }

        function categoryStatusLabel(status?: string) {
            return ({ ready: "準備完了", verified: "確認済み", pending: "反映待ち", warning: "要確認", blocked: "ブロック" } as Record<string, string>)[status || "pending"] || "要確認"
        }

        function measurementPeriodLabel(item: any) {
            const labels = { baseline: "baseline", day_7: "公開後7日", day_28: "公開後28日", day_56: "公開後56日", day_90: "公開後90日", weekly: "週次" } as Record<string, string>
            return `${labels[item.period_type] || item.period_type}（${item.window?.from}〜${item.window?.to}）`
        }

        function outcomeDecisionLabel(value?: string) {
            return ({
                continue: "継続",
                scale: "展開を拡大",
                iterate_title_intro: "タイトル・導入を再検証",
                expand_answer: "回答範囲を拡張",
                add_first_party_evidence: "一次情報を追加",
                improve_internal_link: "内部リンクを改善",
                improve_cta: "CTAを改善",
                resolve_technical_issue: "技術問題を解消",
                rollback: "rollback",
                retire: "終了",
                insufficient_data: "データ不足",
            } as Record<string, string>)[value || ""] || "未判定"
        }

        function searchSurfaceLabel(value: string) {
            return ({ ai_overview: "AI Overview", ai_mode: "AI Mode", organic_search: "通常検索" } as Record<string, string>)[value] || value
        }

        function citationStatusLabel(value: string) {
            return ({ cited: "引用あり", not_cited: "引用なし", unknown: "不明" } as Record<string, string>)[value] || value
        }

        async function poll() {
            const result = await MarketingCampaign.fetchArticleProposal(props.campaignId, props.article.id)
            if (result) emit("update", result)
            if (result?.publication_status == "publishing" || result?.seo_audit_status == "pending") {
                schedulePoll()
            }
        }

        function schedulePoll() {
            if (pollTimer || pollCount >= 40) return
            pollCount += 1
            pollTimer = setTimeout(async () => {
                pollTimer = null
                await poll()
            }, articleStatusPollIntervalMs)
        }

        function checkLabel(code: string) {
            const item = checks.value.find(check => check.code == code)
            return item ? ({ passed: "確認済み", warning: "あとで確認", failed: "要修正", manual_required: "あとで設定" } as Record<string, string>)[item.status] : "未確認"
        }

        function statusMark(status: string) {
            return ({ passed: "✓", warning: "後", failed: "×", manual_required: "後" } as Record<string, string>)[status] || "-"
        }

        watch(
            () => [props.article.publication_status, props.article.seo_audit_status],
            ([publication, audit]) => {
                if (publication == "publishing" || audit == "pending") schedulePoll()
            },
            { immediate: true }
        )

        onUnmounted(() => {
            if (pollTimer) clearTimeout(pollTimer)
        })

        return {
            submitting,
            confirmed,
            errorMessage,
            migrationConfirmed,
            migrationReason,
            migrationRequired,
            reviewDisabled,
            checks,
            conflicts,
            hasPreflight,
            automaticItems,
            manualItems,
            strictQualityGate,
            canPublish,
            auditWarnings,
            auditStatusLabel,
            outcomes,
            outcomesLoading,
            observation,
            observationSaving,
            latestAudit,
            auditCategories,
            reviewPublication,
            adoptWordpress,
            publishArticle,
            recheckSeo,
            loadOutcomes,
            saveObservation,
            categoryMessages,
            categoryStatusLabel,
            measurementPeriodLabel,
            outcomeDecisionLabel,
            searchSurfaceLabel,
            citationStatusLabel,
            checkLabel,
            statusMark,
        }
    },
})
